import VueRouter from 'vue-router'

const { isNavigationFailure, NavigationFailureType } = VueRouter

/**
 * Failures that only say the router ended up somewhere other than where it was sent, not that
 * anything broke: already there, a guard sent it elsewhere, a newer navigation took over, or a
 * guard stopped it. None of them is worth an "Uncaught (in promise)" in the console.
 */
const BENIGN_FAILURES = [
  NavigationFailureType.duplicated,
  NavigationFailureType.redirected,
  NavigationFailureType.cancelled,
  NavigationFailureType.aborted
]

// vue-router 3 compares the type with ===, so a mask of them matches nothing
export const isBenignNavigationFailure = (error) => BENIGN_FAILURES.some((type) => isNavigationFailure(error, type))

/**
 * Make `router.push` / `router.replace` settle quietly on a benign failure.
 *
 * The promise still rejects on a real error (a guard that threw, a chunk that failed to load);
 * the benign failure is what it resolves with, so a caller that cares can still look at it.
 * The callback form is left alone - router-link uses it and handles the failures itself.
 */
export const installSafeNavigation = (router) => {
  const methods = ['push', 'replace']
  methods.forEach((method) => {
    const original = router[method].bind(router)
    router[method] = (location, onComplete, onAbort) => {
      if (onComplete || onAbort) return original(location, onComplete, onAbort)
      return original(location).catch((error) => {
        if (isBenignNavigationFailure(error)) return error
        throw error
      })
    }
  })
  return router
}

/**
 * Whether two queries say the same thing.
 *
 * The router hands a single value back bare and several as an array, and ids come back as
 * text, so '3' and [3] are one filter.
 */
export const sameQuery = (a = {}, b = {}) => {
  const values = (value) => [].concat(value === undefined || value === null ? [] : value).map(String)
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return [...keys].every((key) => values(a[key]).join('\n') === values(b[key]).join('\n'))
}

/**
 * Whether `location` is where the router already stands. The name counts too: the 404 and
 * lock screens keep the address they replace, so the path alone does not tell them apart.
 */
export const isCurrentLocation = (router, location) => {
  const { route } = router.resolve(location, router.currentRoute)
  const current = router.currentRoute
  return route.fullPath === current.fullPath && route.name === current.name
}

/**
 * Go to `location` unless the router is already there.
 *
 * Replacing is for moves the back button should not step through (a redirect, a login screen
 * swapped for another); pushing is for a new place.
 */
export const navigate = (router, location, { replace = false } = {}) => {
  if (isCurrentLocation(router, location)) return Promise.resolve(router.currentRoute)
  return router[replace ? 'replace' : 'push'](location)
}

/**
 * Write the screen's own state (filters, sort, page, tab) into the address of the current
 * route. Replaces, so every keystroke in a search box is not an entry in the history, and says
 * nothing to the router when the address already carries it.
 */
export const replaceQuery = (router, query) => {
  if (sameQuery(query, router.currentRoute.query)) return Promise.resolve(router.currentRoute)
  return router.replace({ query })
}

/** A query without the empty values, so `?term=` does not stay in the address. */
export const compactQuery = (query) =>
  Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== ''))

// --- where the back button goes -------------------------------------------------

const trackers = new WeakMap()
// route being moved to -> 'push' | 'replace' | 'pop' | null (the page load)
const moveKinds = new WeakMap()

const stateKey = () => (window.history.state && window.history.state.key) || null

/**
 * Keep a copy of the session history this app wrote, so a screen can tell where the back
 * button would take the reader.
 *
 * vue-router stamps every entry it pushes with a key and keeps the key on replace, so an
 * entry whose key is already known was either replaced or stepped back / forward to, and an
 * unknown key is a new entry on top of the current one. Entries from before a reload are not
 * known until the reader steps onto them, and then only as the newest ones - the answer for
 * them is "don't know", never a wrong one.
 */
export const trackHistory = (router) => {
  const history = { entries: [], index: -1, kind: null }
  trackers.set(router, history)

  // what kind of move starts next: router.push / replace and a guard's redirect all go through
  // router.history, back / forward arrive as popstate, and the page load is neither
  const kinds = ['push', 'replace']
  kinds.forEach((kind) => {
    const original = router.history[kind].bind(router.history)
    router.history[kind] = (...args) => {
      history.kind = kind
      return original(...args)
    }
  })
  if (router.mode === 'history') {
    window.addEventListener('popstate', () => {
      history.kind = 'pop'
    })
  }
  // the guards of a move run right after it starts, so the first of them can tell which it is
  router.beforeEach((to, from, next) => {
    moveKinds.set(to, history.kind)
    history.kind = null
    next()
  })

  router.afterEach((to) => {
    if (router.mode === 'abstract') return
    const entry = { key: stateKey(), fullPath: to.fullPath }
    const known = history.entries.findIndex(({ key }) => key === entry.key)
    if (known !== -1) {
      history.entries[known] = entry
      history.index = known
    } else {
      history.entries = history.entries.slice(0, history.index + 1).concat(entry)
      history.index = history.entries.length - 1
    }
  })

  return router
}

/**
 * The location a guard should redirect `to` to.
 *
 * A guard's redirect pushes by default. That is right when the reader clicked a link: the
 * stopped move never got an entry. It is wrong when the address is already in the history -
 * the page load, back / forward, a replace - because the stopped address stays behind the
 * target, and back returns to it only for the guard to redirect again: a trap. Those redirects
 * replace the entry instead.
 */
export const redirectLocation = (to, location) => {
  const target = typeof location === 'string' ? { path: location } : location
  return moveKinds.get(to) === 'push' ? target : { ...target, replace: true }
}

/** The full path of the entry the back button would open, or null when that is not known. */
export const previousPath = (router) => {
  if (router.mode === 'abstract') {
    const { stack, index } = router.history
    return index > 0 ? stack[index - 1].fullPath : null
  }
  const history = trackers.get(router)
  if (!history || history.index < 1) return null
  return history.entries[history.index - 1].fullPath
}

/**
 * Leave a screen for the one it was opened from - closing an edit modal onto its list, closing
 * a desk panel onto the bare floor.
 *
 * When the reader came from exactly there, this is a step back: pushing would leave the list
 * in the history twice and the back button would reopen the modal. Otherwise the current
 * entry is replaced, so the closed screen is not an entry the back button returns to.
 */
export const returnTo = (router, location) => {
  if (isCurrentLocation(router, location)) return Promise.resolve(router.currentRoute)

  const { route } = router.resolve(location, router.currentRoute)
  if (previousPath(router) === route.fullPath) {
    return new Promise((resolve) => {
      const stop = router.afterEach((to) => {
        stop()
        resolve(to)
      })
      router.back()
    })
  }
  return router.replace(location)
}

/** Leave the app for another address - another domain, a server-side login. */
export const leaveApp = (url) => {
  window.location = url
}

// --- the catch-all screens ------------------------------------------------------

// both screens keep the address the reader asked for: the catch-all param is that path, less
// the leading slash the route itself puts back
const catchAll = (name, path) => ({ name, params: { pathMatch: String(path || '').replace(/^\/+/, '') } })
export const notFoundLocation = (path) => catchAll('404', path)
export const lockedLocation = (path) => catchAll('lock', path)
